import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { OrderBreakdownItem } from '../../hooks/useKPIs'

// Palette coerente col design system (indigo→purple→cyan→emerald→amber→red→pink→slate)
const COLORS = [
  '#6366f1', // indigo-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#ec4899', // pink-500
  '#64748b', // slate-500 — riservato a "Altri"
]

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

interface SingleChartProps {
  title: string
  data: OrderBreakdownItem[]
}

function SingleDonut({ title, data }: SingleChartProps) {
  const isEmpty = data.length === 0

  return (
    <div className="km-panel p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">{title}</p>
      </div>

      {isEmpty ? (
        <div className="flex flex-1 items-center justify-center h-[240px] text-[--km-text-disabled] text-sm">
          Nessun ordine confermato
        </div>
      ) : (
        <div className="w-full" style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={data.map(d => ({ ...d, value: +d.value }))}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="40%"
              innerRadius={60}
              outerRadius={92}
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((_, index) => (
                <Cell
                  key={index}
                  fill={COLORS[index % COLORS.length]}
                  opacity={0.88}
                />
              ))}
            </Pie>

            <Tooltip
              formatter={(value: number, _name: string, props) => [
                `${formatEur(value)} (${props.payload?.pct?.toFixed(1)}%)`,
                props.payload?.name,
              ]}
              contentStyle={{
                background: 'rgba(13, 19, 33, 0.95)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                fontSize: '12px',
              }}
              labelStyle={{ display: 'none' }}
              itemStyle={{ color: '#f1f5f9' }}
            />

            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value: string) => (
                <span style={{ fontSize: '11px', color: 'var(--km-text-secondary)' }}>
                  {value}
                </span>
              )}
              wrapperStyle={{ paddingTop: '12px' }}
            />
          </PieChart>
        </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

interface OrdersBreakdownChartsProps {
  byCustomer: OrderBreakdownItem[]
  byServiceLine: OrderBreakdownItem[]
}

export function OrdersBreakdownCharts({ byCustomer, byServiceLine }: OrdersBreakdownChartsProps) {
  return (
    <div
      className="grid grid-cols-1 gap-5 md:grid-cols-2"
      data-testid="orders-breakdown-charts"
    >
      <SingleDonut title="Ordinato per Cliente" data={byCustomer} />
      <SingleDonut title="Ordinato per Service Line" data={byServiceLine} />
    </div>
  )
}
